import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Switch, toast } from "@sagot/ui";
import { Controller, FormProvider, useFieldArray, useForm, useWatch, type Control } from "react-hook-form";
import { FormAlert } from "../../../auth/components/form-alert";
import { useErrorMessage } from "../../../lib/api-errors";
import { zodResolver } from "../../../lib/zod-resolver";
import type { Action } from "../shared/types";
import { BookingFormFieldRow } from "./booking-form-field";
import { EMPTY_FIELD, FORM_LIMITS, bookingFormFromAction, toBookingForm, useBookingFormSchema, type BookingFormValues } from "./booking-form-model";
import { InvitePreview } from "./invite-preview";
import { useActionMutations } from "./use-actions";

const P = "build.actions.bookingForm";

function ToggleRow({ control, name, disabled }: { control: Control<BookingFormValues>; name: "ask_phone" | "ask_notes"; disabled: boolean }) {
  const { t } = useTranslation();
  const key = name === "ask_phone" ? "askPhone" : "askNotes";
  const id = `booking-form-${name}`;
  return (
    <div className="flex items-start justify-between gap-4 p-4">
      <div>
        <label htmlFor={id} className="text-sm font-semibold">{t(`${P}.${key}`)}</label>
        <p id={`${id}-hint`} className="mt-0.5 text-xs text-fg-subtle">{t(`${P}.${key}Hint`)}</p>
      </div>
      <Controller control={control} name={name} render={({ field }) => (
        <Switch id={id} aria-describedby={`${id}-hint`} checked={field.value} onCheckedChange={field.onChange} disabled={disabled} className="mt-0.5" />
      )} />
    </div>
  );
}

interface BookingFormEditorProps {
  agentId: string;
  action: Action;
  canManage: boolean;
  onSaved: (action: Action) => void;
}

/** Booking form of a booking action: what visitors fill in; every answer goes into the invitation. */
export function BookingFormEditor({ agentId, action, canManage, onSaved }: BookingFormEditorProps) {
  const { t } = useTranslation();
  const message = useErrorMessage();
  const { update } = useActionMutations(agentId);
  const form = useForm<BookingFormValues>({ resolver: zodResolver(useBookingFormSchema()), defaultValues: bookingFormFromAction(action) });
  const rows = useFieldArray({ control: form.control, name: "fields" });
  const values = useWatch({ control: form.control }) as BookingFormValues;
  const disabled = !canManage;
  const full = rows.fields.length >= FORM_LIMITS.fields;
  const submit = form.handleSubmit((data) => {
    update.mutate(
      { id: action.id, body: { booking_form: toBookingForm(data) } },
      {
        onSuccess: (saved) => {
          form.reset(bookingFormFromAction(saved));
          toast.success(t(`${P}.saved`));
          onSaved(saved);
        },
      },
    );
  });
  return (
    <section aria-labelledby="booking-form-title" className="space-y-5 rounded-lg border border-line p-4" data-testid="booking-form-editor">
      <div>
        <h3 id="booking-form-title" className="flex items-center gap-2 text-base font-bold">
          <Icon name="list-checks" className="size-4 text-primary-ink" />
          {t(`${P}.title`)}
        </h3>
        <p className="mt-0.5 text-xs text-fg-subtle">{t(`${P}.subtitle`)}</p>
      </div>
      <FormProvider {...form}>
        <form id="booking-form-form" noValidate className="space-y-5" onSubmit={(event) => void submit(event)}>
          {update.isError ? <FormAlert title={message(update.error)} /> : null}
          {disabled ? <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted">{t(`${P}.readOnly`)}</p> : null}
          <p className="flex items-start gap-2 rounded-md bg-info-soft p-3 text-xs text-fg-muted">
            <Icon name="check-circle" className="mt-0.5 size-3.5 shrink-0" />
            {t(`${P}.fixed`)}
          </p>
          <div className="divide-y divide-line rounded-lg border border-line">
            <ToggleRow control={form.control} name="ask_phone" disabled={disabled} />
            <ToggleRow control={form.control} name="ask_notes" disabled={disabled} />
          </div>
          <div>
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <span className="label mb-0">{t(`${P}.fields`)}</span>
              <Button type="button" variant="secondary" size="sm" disabled={disabled || full} onClick={() => { rows.append({ ...EMPTY_FIELD }); }}>
                <Icon name="plus" />
                {t(`${P}.addField`)}
              </Button>
            </div>
            {rows.fields.length === 0 ? <p className="rounded-md border border-dashed border-line p-4 text-center text-xs text-fg-subtle">{t(`${P}.empty`)}</p> : null}
            <ol className="space-y-3">
              {rows.fields.map((row, index) => (
                <BookingFormFieldRow key={row.id} index={index} count={rows.fields.length} disabled={disabled} onMove={rows.move} onRemove={rows.remove} />
              ))}
            </ol>
            {form.formState.errors.fields?.message ? <p className="mt-2 text-xs font-semibold text-danger" role="alert">{form.formState.errors.fields.message}</p> : null}
          </div>
          <InvitePreview values={values} />
          {canManage ? (
            <div className="flex justify-end">
              <Button type="submit" loading={update.isPending}>
                <Icon name="check" />
                {t(`${P}.save`)}
              </Button>
            </div>
          ) : null}
        </form>
      </FormProvider>
    </section>
  );
}
